import { useState } from 'react'
import { ClipboardList, Loader2, RefreshCw, ChevronDown, ChevronRight, AlertCircle, CheckCircle, XCircle, Link2 } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useActionLog } from '@/hooks/useApi'
import { timeAgo } from '@/lib/utils'

const RESULT_CONFIG: Record<string, { color: string; icon: typeof CheckCircle }> = {
  success: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  failure: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
  cancelled: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: AlertCircle },
}

export function ActionLogPanel() {
  const [resultFilter, setResultFilter] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data, isLoading, refetch } = useActionLog(resultFilter || undefined, undefined, searchTerm || undefined)
  const entries = data?.entries ?? []

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            Action Log
            {isLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            <div className="ml-auto flex items-center gap-2">
              {data && <Badge variant="outline">{data.count} entries</Badge>}
              <button
                onClick={() => refetch()}
                className="p-1.5 text-text-muted hover:text-text-primary transition-colors rounded"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4 flex-wrap items-center">
            <input
              type="text"
              placeholder="Search actions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-1.5 text-xs rounded-lg border border-border bg-surface-elevated text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent/50 w-48"
            />
            {['', 'success', 'failure', 'cancelled'].map((r) => (
              <button
                key={r}
                onClick={() => setResultFilter(r)}
                className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${resultFilter === r ? 'bg-accent/20 text-accent border-accent/30' : 'text-text-muted border-border hover:border-accent/30'}`}
              >
                {r || 'All'}
              </button>
            ))}
          </div>

          {entries.length === 0 && !isLoading && (
            <div className="text-center py-8 text-text-muted text-sm">
              No action log entries found.
            </div>
          )}

          <div className="space-y-1">
            {entries.map((entry) => {
              const rc = RESULT_CONFIG[entry.result] || RESULT_CONFIG.success
              const ResultIcon = rc.icon
              const isExpanded = expandedId === entry.action_id
              return (
                <div key={entry.action_id}>
                  <div
                    onClick={() => setExpandedId(isExpanded ? null : entry.action_id)}
                    className="flex items-center gap-3 p-2.5 rounded-lg border border-border hover:border-accent/30 transition-colors cursor-pointer bg-surface-elevated"
                  >
                    {isExpanded ? <ChevronDown className="h-3 w-3 shrink-0 text-text-muted" /> : <ChevronRight className="h-3 w-3 shrink-0 text-text-muted" />}
                    <span className="text-[10px] font-mono text-text-muted w-24 shrink-0">{entry.action_id}</span>
                    <span className="text-[10px] text-text-muted w-28 shrink-0">{timeAgo(new Date(entry.timestamp))}</span>
                    <span className="text-xs text-text-primary font-mono truncate flex-1">{entry.action}</span>
                    <span className="text-xs text-text-muted truncate max-w-[200px]">{entry.target || '-'}</span>
                    <Badge className={`text-[10px] border ${rc.color}`}>
                      <ResultIcon className="h-2.5 w-2.5 mr-1" />
                      {entry.result}
                    </Badge>
                    {entry.parent_id && <Link2 className="h-3 w-3 text-text-muted" aria-label="Part of chain" />}
                  </div>
                  {isExpanded && (
                    <div className="ml-6 mt-1 mb-2 p-3 rounded-lg bg-surface-elevated/50 border border-border/50 space-y-2 text-sm">
                      {entry.reason && (
                        <div className="flex gap-2">
                          <span className="text-text-muted w-24 shrink-0">Reason</span>
                          <span className="text-text-primary">{entry.reason}</span>
                        </div>
                      )}
                      {entry.user_request && (
                        <div className="flex gap-2">
                          <span className="text-text-muted w-24 shrink-0">Request</span>
                          <span className="text-text-primary text-xs">{entry.user_request}</span>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <span className="text-text-muted w-24 shrink-0">Actor</span>
                        <span className="text-text-primary">{entry.actor}</span>
                      </div>
                      {entry.session_id && (
                        <div className="flex gap-2">
                          <span className="text-text-muted w-24 shrink-0">Session</span>
                          <span className="text-text-primary font-mono text-xs">{entry.session_id}</span>
                        </div>
                      )}
                      {entry.commit_sha && (
                        <div className="flex gap-2">
                          <span className="text-text-muted w-24 shrink-0">Commit</span>
                          <span className="text-text-primary font-mono text-xs">{entry.commit_sha}</span>
                        </div>
                      )}
                      {entry.error_detail && (
                        <div className="flex gap-2">
                          <span className="text-text-muted w-24 shrink-0">Error</span>
                          <span className="text-red-400 text-xs">{entry.error_detail}</span>
                        </div>
                      )}
                      {entry.attempt > 1 && (
                        <div className="flex gap-2">
                          <span className="text-text-muted w-24 shrink-0">Attempt</span>
                          <span className="text-text-primary">{entry.attempt}</span>
                        </div>
                      )}
                      {entry.parent_id && (
                        <div className="flex gap-2">
                          <span className="text-text-muted w-24 shrink-0">Parent</span>
                          <span className="text-text-primary font-mono text-xs">{entry.parent_id}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
